import { normalizeApiError, normalizeError } from 'core/common/errors';
import { CookiesService } from 'core/common/services/cookie';
import { FunnelSettingsServiceInterface } from 'core/common/services/funnel-settings';
import { Logger } from 'core/common/services/logger/interfaces';
import { getCurrencyMinorUnits } from 'core/common/utils/getCurrencyMinorUnits';
import { minorUnitsToMajor } from 'core/common/utils/money';
import {
  CardBrand,
  CardPaymentEntities,
  CardType,
  Currency,
  PaymentOrderDescription,
  PaymentType,
} from 'core/payments/entities';
import { UtmTags } from 'core/user/entities';
import { UtmTagsService } from 'core/user/services';
import {
  ConversionsApiAttributionParameters,
  AnalyticsEventProperties,
  AnalyticsEventValue,
  FunnelProperties,
  PaymentFormCompletedProperties,
  PaymentFormStateProperties,
} from '../../entities';
import {
  AmplitudeService,
  AnalyticsService,
  AppnebulaBingAdsService,
  AppnebulaFacebookService,
  AppnebulaSnapchatService,
  AppnebulaTaboolaService,
  AppnebulaTikTokService,
  BackendAnalyticsService,
  GTMService,
  LtvPredictService,
  SnitcherService,
} from '../../interfaces';
import { ConversionsAPIService } from '../conversionsAPI';

const cardPaymentEntityToPaymentMethod: Record<CardPaymentEntities, PaymentType> = {
  [CardPaymentEntities.APPLE_BTN]: PaymentType.APPLE_PAY,
  [CardPaymentEntities.GOOGLE_BTN]: PaymentType.GOOGLE_PAY,
  [CardPaymentEntities.FORM]: PaymentType.CARD,
};

const toPaymentMethod = (paymentEntity?: string) =>
  paymentEntity && paymentEntity in cardPaymentEntityToPaymentMethod
    ? cardPaymentEntityToPaymentMethod[paymentEntity as CardPaymentEntities]
    : paymentEntity;

export class AnalyticsClientService implements AnalyticsService {
  private userId: string;

  constructor(
    private readonly amplitudeService: AmplitudeService,
    private readonly conversionsAPIService: ConversionsAPIService,
    private readonly tikTokService: AppnebulaTikTokService,
    private readonly facebookService: AppnebulaFacebookService,
    private readonly gtmService: GTMService,
    private readonly utmTagsService: UtmTagsService,
    private readonly ltvPredictService: LtvPredictService,
    private readonly backendAnalyticsService: BackendAnalyticsService,
    private readonly logger: Logger,
    private readonly bingAdsService: AppnebulaBingAdsService,
    private readonly taboolaService: AppnebulaTaboolaService,
    private readonly cookiesService: CookiesService,
    private readonly funnelSettingsService: FunnelSettingsServiceInterface,
    private readonly snapchatService: AppnebulaSnapchatService,
    private readonly snitcherService: SnitcherService,
  ) {
    this.userId = this.loadUserIdFromStorage();
    this.setUtmTags(this.utmTagsService.getUtmTags());
    this.checkCookieAccess();
  }

  getAttributionParameters(): ConversionsApiAttributionParameters {
    return {
      ...this.conversionsAPIService.getAttributionParameters(),
    };
  }

  getDeviceId() {
    return this.amplitudeService.getDeviceId();
  }

  getSessionId() {
    return this.amplitudeService.getSessionId();
  }

  trackEvent(event: AnalyticsEventValue, properties: AnalyticsEventProperties = {}) {
    this.amplitudeService.trackEvent(event, properties);
    this.snitcherService.trackEvent(event, properties);
  }

  funnelOpened(properties: FunnelProperties) {
    this.tikTokService.onFunnelOpened();
    this.amplitudeService.setFunnelProperties(properties);
  }

  addUserAssociatedId(associatedId: string) {
    this.amplitudeService.setUserProperties({ associated_id: associatedId });
  }

  optOtSellUserData() {
    this.gtmService.optOtSellUserData();
  }

  updateUserProperties(properties: Record<string, unknown>) {
    this.amplitudeService.setUserProperties(properties);
  }

  setUserId(userId: string) {
    this.amplitudeService.setUserId(userId);
    this.userId = userId;
  }

  setIsUserTester(isTester: boolean) {
    this.amplitudeService.setUserProperties({
      tester: isTester,
    });
  }

  // Meta conversion events are sent by the backend only; the browser must not duplicate them
  paymentPlanChosen() {}

  paymentFormOpened() {}

  techPageOpened(page: string, context: string) {
    this.amplitudeService.techPageOpened({ page, context, userId: this.userId });
  }

  setUserExperimentGroupName(experimentName: string, groupName: string) {
    try {
      this.backendAnalyticsService.setSplitExperimentProperties({ experimentName, groupName });
    } catch (e) {
      const error = normalizeError(e);

      this.logger.error(error);
    }
  }

  trackPaymentFormState(properties: PaymentFormStateProperties) {
    const paymentFormStateProperties = {
      ...properties,
      payment_method: toPaymentMethod(properties.payment_method),
    };

    this.amplitudeService.trackPaymentFormState(paymentFormStateProperties);
    this.snitcherService.trackPaymentFormState(paymentFormStateProperties);
  }

  paymentMethodClose(paymentType: PaymentType) {
    this.trackPaymentFormState({ state: 'close_method', payment_method: paymentType });
  }

  paymentError({
    reason,
    productId,
    paymentMethod,
  }: {
    reason: string;
    productId: string;
    paymentMethod: string;
  }) {
    const properties = {
      state: 'error',
      reason,
      product_id: productId,
      payment_method: toPaymentMethod(paymentMethod),
    } satisfies PaymentFormCompletedProperties;

    this.amplitudeService.setUserProperties({ user_id: this.userId });
    this.amplitudeService.paymentFormCompleted(properties);
    this.snitcherService.paymentFormCompleted(properties);
  }

  async paymentSuccess(paymentData: {
    productId: string;
    paymentProviderOrder: PaymentOrderDescription;
    orderId: string;
    paymentId: string;
    paymentMethod: string;
    email: string;
    paymentCardBrand: CardBrand;
    paymentCardType: CardType;
    nebulaId: string;
  }) {
    const {
      productId,
      paymentProviderOrder,
      orderId,
      paymentId,
      email,
      paymentCardBrand,
      paymentCardType,
      nebulaId,
    } = paymentData;

    const paymentMethod = toPaymentMethod(paymentData.paymentMethod);

    this.amplitudeService.setUserProperties({
      product_id: productId,
      payment_method: paymentMethod,
    });
    const paymentCompletedProperties = {
      state: 'success',
      product_id: productId,
      payment_method: paymentMethod,
      card_brand: paymentCardBrand,
      card_type: paymentCardType,
    } satisfies PaymentFormCompletedProperties;

    this.amplitudeService.paymentFormCompleted(paymentCompletedProperties);
    this.snitcherService.paymentFormCompleted({ ...paymentCompletedProperties, order_id: orderId });

    const { amount, currency } = paymentProviderOrder;
    const minorUnits = getCurrencyMinorUnits(currency);
    const price = minorUnitsToMajor(amount, minorUnits);
    const ltvPredict = await this.ltvPredictService.getLTVPredict({
      amountMinorUnits: amount,
      currency,
      cardType: paymentCardType,
      cardBrand: paymentCardBrand,
    });

    try {
      this.gtmService.purchase({
        price,
        orderId: paymentProviderOrder.order_id,
        productId,
        ltvPredict,
        currency,
        email,
        paymentId,
        nebulaId,
      });

      // GS-7912: browser TikTok CompletePayment is temporarily disabled, purchases are tracked server-side
      // if (!this.funnelSettingsService.excludeFunnels(['ex-compatibility'])) {
      //   this.tikTokService.paymentSuccess({
      //     price,
      //     orderId,
      //     productId,
      //     ltvPredict,
      //     currency,
      //     email,
      //   });
      // }

      this.bingAdsService.purchase({
        price,
        orderId,
        productId,
        ltvPredict,
        currency,
        email,
      });
      this.taboolaService.purchase({
        currency,
        ltvPredict,
        orderId,
      });
      this.snapchatService.purchase({
        price,
        ltvPredict,
        paymentId,
        currency,
        productId,
      });
    } catch (e) {
      const error = normalizeError(e);
      this.logger.error(error);
    }
  }

  paymentRetryScreenOpened() {
    this.amplitudeService.paymentRetryScreenOpened();
  }

  paymentRetryTryAgainClicked() {
    this.amplitudeService.paymentRetryTryAgainClicked();
  }

  paymentRetryCloseClicked() {
    this.amplitudeService.paymentRetryCloseClicked();
  }

  viewSubscriptions({
    items,
    currency,
    nebulaId,
  }: {
    items: Array<{ productId: string; price: number }>;
    currency: Currency;
    nebulaId: string;
  }) {
    this.gtmService.viewItem({ items, currency });
    this.snapchatService.viewContent({ items, currency, nebulaId });
  }

  addToCart({
    price,
    trialPrice,
    productId,
    email,
    orderId,
    currency,
    nebulaId,
  }: {
    price: number;
    trialPrice?: number;
    productId: string;
    email: string;
    orderId: string;
    currency: Currency;
    nebulaId: string;
  }) {
    this.gtmService.addToCart({ price, productId, email, orderId, currency });
    this.tikTokService.addToCard({ price, productId, email, orderId, currency });
    this.snapchatService.addToCart({ price, productId, currency, nebulaId });

    if (trialPrice) {
      this.bingAdsService.addToCart({ productId, email, orderId, currency, trialPrice });
    }
  }

  initiateCheckout({
    price,
    productId,
    email,
    orderId,
    currency,
    trialPrice,
    nebulaId,
  }: {
    price: number;
    productId: string;
    email: string;
    orderId: string;
    currency: Currency;
    trialPrice?: number;
    nebulaId: string;
  }) {
    this.gtmService.initiateCheckout({ price, productId, email, orderId, currency });
    this.tikTokService.initiateCheckout({ price, productId, email, orderId, currency });
    this.snapchatService.startCheckout({ price, productId, currency, nebulaId });

    if (trialPrice) {
      this.bingAdsService.beginCheckout({ trialPrice, productId, email, orderId, currency });
    }
  }

  addEmail(email: string, userId: string) {
    this.gtmService.addEmail(email, userId);
    this.tikTokService.addEmail(email, userId);
    this.taboolaService.signUp();
    this.snapchatService.signUp(email, userId);
    this.amplitudeService.setUserEmail(email);
  }

  addUserId(id: string) {
    this.amplitudeService.setUserIdProperty(id);
  }

  purchaseSucceed({
    productId,
    paymentMethod,
    paymentCardBrand,
    paymentCardType,
    orderId,
  }: {
    productId: string;
    paymentMethod: string;
    paymentCardBrand: CardBrand;
    paymentCardType: CardType;
    orderId: string;
  }) {
    const properties = {
      state: 'success',
      product_id: productId,
      payment_method: toPaymentMethod(paymentMethod),
      card_brand: paymentCardBrand,
      card_type: paymentCardType,
    } satisfies PaymentFormCompletedProperties;

    this.amplitudeService.paymentFormCompleted(properties);
    this.snitcherService.paymentFormCompleted({ ...properties, order_id: orderId });
  }

  setUserAge(age: number) {
    this.amplitudeService.setUserAge(age);
  }

  setFeatureFlagsAttributes(attributes: Record<string, unknown>) {
    this.amplitudeService.setFeatureFlagsAttributes(attributes);
  }

  async displayPaywallConditions(email: string) {
    this.amplitudeService.trackEvent('funnel_screen_scroll', {
      screen_context: 'trial_billing',
      scroll_details: 'terms_and_conditions_displayed',
    });
    await this.backendAnalyticsService.trackEvent('paywall_terms_and_conditions_displayed', {
      email,
    });
  }

  async acceptConditions(email: string) {
    this.amplitudeService.trackEvent('funnel_screen_click', {
      screen_context: 'trial_billing',
      value: 'terms_accepted',
    });
    await this.backendAnalyticsService.trackEvent('terms_and_conditions_accepted', {
      email,
    });
  }

  private loadUserIdFromStorage(): string {
    try {
      const userStorage = localStorage.getItem('nebula') || '';
      const userId = userStorage ? JSON.parse(userStorage).userProfile.id : '';
      return userId;
    } catch (err) {
      const error = normalizeApiError(err);
      this.logger.error(error);
      return '';
    }
  }

  private checkCookieAccess() {
    const checkCookieKey = 'checkAccessToCookie';
    const checkCookieValue = 'allow';
    const checkCookieOptions = { path: '/' };

    this.cookiesService.set(checkCookieKey, checkCookieValue, checkCookieOptions);
    const isHasAccessToCookie = this.cookiesService.get(checkCookieKey) === checkCookieValue;
    this.amplitudeService.setUserProperties({ is_has_access_to_cookie: isHasAccessToCookie });
    this.cookiesService.remove(checkCookieKey, checkCookieOptions);
  }

  private setUtmTags(tags: UtmTags) {
    this.amplitudeService.setUtmTags(tags);
  }
}
